import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useLocation
} from 'react-router-dom';
import analyticsManager from '@lmig/crab-rangoons';
import FooterComponent from './features/layout/components/footer/footer-component';
import HeaderComponent from './features/layout/components/header/header-component';
import AppLayout from './features/layout/components/app-layout';
import GlobalBanners from './features/layout/components/global-banner/global-banners';
import ClaimStatus from './features/claim-status/claim-status-page';
import Registration from './features/registration/registration-page';
import RegistrationConfirmationPage from './features/registration/components/registration-confirmation-page';
import RegistrationErrorPage from './features/registration/components/registration-error-page';
import { useAuth0Authentication } from './hooks/use-auth0-authentication';
import { authService } from './features/common/services';
import CallBackPage from './features/common/callback-page';
import ClaimSearchPage from './features/claim-search/claim-search-page';
import env from './utils/env';
import { Envs } from './features/common/models/generic-enum';
import { rumAnalytics } from './utils/rum-analytics';
import { trackCustomHeapEvent } from './utils/heap';
import PageTracker from './page-tracker';
import routesConfig from './routes-config';
import Settings from './features/mfa/components/settings-page';
import LearnMore from './features/mfa/components/learn-more-page';
import ResetMfaSuccess from './features/mfa/components/reset-mfa-success';
import './app.scss';

const PublicRoute = ({ children }: any) => {
  const [isAuthenticated] = useAuth0Authentication();
  if (
    !(env.appEnv === Envs.Local || env.appEnv === Envs.Demo) &&
    isAuthenticated
  ) {
    // user is logged in, log the user out logged in users should not access
    authService.logout();
  }
  return children;
};

const PrivateRoute = ({ children }: any) => {
  const [isAuthenticated] = useAuth0Authentication();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/callback" state={{ from: location }} replace />;
  }

  if (!(env.appEnv === Envs.Local || env.appEnv === Envs.Demo)) {
    if (isAuthenticated && !authService.isAuthorized()) {
      trackCustomHeapEvent('unauthorized-user-role', {});
      return authService.logout();
    }
  }
  return children;
};


const NotFoundRoute = () : any  => {
  const isAuthorized = authService.isAuthorized();
  const location = useLocation();
 
  if (env.appEnv === Envs.Local || env.appEnv === Envs.Demo || isAuthorized) {
    return <Navigate to="/claim-search" state={{ from: location }} replace />;
  } else {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
};

function App() {
  rumAnalytics();
  analyticsManager.init(null, {
    galileoUrl: env.galileoUrl,
    tealiumUrl: env.tealiumUrl,
    pageName: 'ClaimInformationPortal',
    debugLogging: env.appEnv === Envs.Local || env.appEnv === Envs.Demo || env.appEnv === Envs.Staging
  });

  const {
    CLAIM_STATUS: { path: claimStatusPath },
    REGISTRATION: { path: registrationPath },
    CONFIRMATION: { path: confirmationPath },
    ERROR: { path: errorPath },
    CLAIM_SEARCH: { path: claimSearchPath },
    ROOT: { path: rootPath },
    SETTINGS: { path: settings },
    LEARN_MORE: { path: learnMore },
    RESET_MFA_SUCCESS: { path: resetMfaSuccess}
    // Destructure other routes as required
  } = routesConfig;

  return (
    <BrowserRouter basename={'/'}>
      <PageTracker />
      <AppLayout
        banners={<GlobalBanners />}
        header={<HeaderComponent />}
        content={
          <Routes>
            {/* PUBLIC ROUTES */}
            <Route
              path={rootPath}
              element={
                <PublicRoute>
                  <CallBackPage />
                </PublicRoute>
              }
            />
            <Route path="/callback" element={<CallBackPage />} />
            <Route
              path={claimStatusPath}
              element={
                <PublicRoute>
                  <ClaimStatus />
                </PublicRoute>
              }
            />
            <Route
              path={registrationPath}
              element={
                <PublicRoute>
                  <Registration />
                </PublicRoute>
              }
            />
            <Route
              path={confirmationPath}
              element={
                <PublicRoute>
                  <RegistrationConfirmationPage />
                </PublicRoute>
              }
            />
            <Route
              path={errorPath}
              element={
                <PublicRoute>
                  <RegistrationErrorPage />
                </PublicRoute>
              }
            />
            {/* PRIVATE ROUTES */}
            <Route
              path={claimSearchPath}
              element={
                <PrivateRoute>
                  <ClaimSearchPage />
                </PrivateRoute>
              }
            />
            <Route
              path={settings}
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path={learnMore}
              element={
                <PrivateRoute>
                  <LearnMore />
                </PrivateRoute>
              }
            />
            <Route
              path={resetMfaSuccess}
              element={
                <PrivateRoute>
                  <ResetMfaSuccess />
                </PrivateRoute>
              }
            />
            {/* ERROR ROUTES */}
            <Route path='*' element={
              <NotFoundRoute />
            } 
            />
          </Routes>
        }
        footer={<FooterComponent />}
      />
    </BrowserRouter>
  );
}

export default App;


